import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/apiClient";
import type { Shift } from "../api/types";
import { ClockInOutCard } from "../components/ClockInOutCard";
import { VacationsCard } from "../components/profile/VacationsCard";
import { formatTime24 } from "../utils/time24";

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatRange(s: Shift): string {
  const d = new Date(s.plannedStart);
  const datePart = d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return `${datePart}, ${formatTime24(s.plannedStart)}–${formatTime24(s.plannedEnd)}`;
}

// Staff-only page: couriers were removed from the shifts/vacations plane by
// owner decision (G-2 / D-16) — their surface is /app/courier/calendar.
export function MyShiftsPage() {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const today = new Date();
      const from = new Date(today);
      from.setDate(today.getDate() - 30);
      const to = new Date(today);
      to.setDate(today.getDate() + 30);
      const list = await api.listMyShifts({
        from: toIsoDate(from),
        to: toIsoDate(to),
      });
      setShifts(list);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load shifts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const today = useMemo(() => new Date(), []);

  const todayShifts = useMemo(
    () =>
      shifts
        .filter((s) => isSameDay(new Date(s.plannedStart), today))
        .sort(
          (a, b) =>
            new Date(a.plannedStart).getTime() -
            new Date(b.plannedStart).getTime(),
        ),
    [shifts, today],
  );

  const upcoming = useMemo(
    () =>
      shifts
        .filter(
          (s) =>
            new Date(s.plannedStart).getTime() > today.getTime() &&
            !isSameDay(new Date(s.plannedStart), today),
        )
        .sort(
          (a, b) =>
            new Date(a.plannedStart).getTime() -
            new Date(b.plannedStart).getTime(),
        )
        .slice(0, 10),
    [shifts, today],
  );

  const past = useMemo(
    () =>
      shifts
        .filter(
          (s) =>
            new Date(s.plannedEnd).getTime() < today.getTime() &&
            !isSameDay(new Date(s.plannedStart), today),
        )
        .sort(
          (a, b) =>
            new Date(b.plannedStart).getTime() -
            new Date(a.plannedStart).getTime(),
        )
        .slice(0, 10),
    [shifts, today],
  );

  const replaceShift = (next: Shift) => {
    setShifts((cur) => cur.map((s) => (s.id === next.id ? next : s)));
  };

  return (
    <section
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-4)",
      }}
    >
      <div>
        <h2 className="page-title">My shifts</h2>
        <p className="page-subtitle">
          Clock in when you start your shift and clock out when you finish. Your
          manager can correct the actual times later if you forget.
        </p>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <VacationsCard />

      <div>
        <h3 style={{ marginBottom: "var(--space-2)" }}>Today</h3>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : todayShifts.length === 0 ? (
          <div className="card">
            <p className="muted" style={{ margin: 0 }}>
              No shift on the calendar for today.
            </p>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-3)",
            }}
          >
            {todayShifts.map((s) => (
              <ClockInOutCard
                key={s.id}
                shift={s}
                onChanged={replaceShift}
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 style={{ marginBottom: "var(--space-2)" }}>Upcoming</h3>
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table>
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Shop</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.length === 0 ? (
                <tr>
                  <td colSpan={3} className="empty-state">
                    Nothing scheduled in the next 30 days.
                  </td>
                </tr>
              ) : (
                upcoming.map((s) => (
                  <tr key={s.id}>
                    <td>{formatRange(s)}</td>
                    <td>
                      {s.location.name}{" "}
                      <span className="muted">({s.location.code})</span>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          s.status === "SCHEDULED" ? "muted" : "primary"
                        }`}
                      >
                        {s.status.replace("_", " ").toLowerCase()}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 style={{ marginBottom: "var(--space-2)" }}>Past</h3>
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table>
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Shop</th>
                <th>Planned</th>
                <th>Actual</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {past.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty-state">
                    No completed shifts yet in the last 30 days.
                  </td>
                </tr>
              ) : (
                past.map((s) => {
                  const planned = `${formatTime24(s.plannedStart)}–${formatTime24(s.plannedEnd)}`;
                  const actual =
                    s.actualStart && s.actualEnd
                      ? `${formatTime24(s.actualStart)}–${formatTime24(s.actualEnd)}`
                      : "—";
                  return (
                    <tr key={s.id}>
                      <td>
                        {new Date(s.plannedStart).toLocaleDateString(
                          undefined,
                          {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                          },
                        )}
                      </td>
                      <td>
                        {s.location.name}{" "}
                        <span className="muted">({s.location.code})</span>
                      </td>
                      <td>{planned}</td>
                      <td>{actual}</td>
                      <td>
                        <span
                          className={`badge ${
                            s.status === "COMPLETED"
                              ? "success"
                              : s.status === "MISSED"
                                ? "muted"
                                : "primary"
                          }`}
                        >
                          {s.status.replace("_", " ").toLowerCase()}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
